import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { UserPlus, Shield, Eye, Edit3, Trash2, Check, X, RefreshCw } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api/superadmin/sa-users';

const ROLE_CONFIG = {
  owner: { label: 'Owner', color: '#c9a84c', bg: 'rgba(201,168,76,0.1)' },
  editor: { label: 'Editor', color: '#7c3aed', bg: 'rgba(124,58,237,0.1)' },
  viewer: { label: 'Viewer', color: '#0891b2', bg: 'rgba(8,145,178,0.1)' },
};

const PERMISSION_LABELS = {
  tenants: 'Tenant', billing: 'Billing', leads: 'Lead CRM',
  marketing: 'Marketing', system: 'Sistema', features: 'Feature Flags', sa_users: 'Admin Utenti',
};

const EMPTY_FORM = { email: '', name: '', password: '', role: 'viewer', permissions: [] };

export function AdminUsersTab({ storedKey }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const headers = { 'X-Superadmin-Key': storedKey };

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(API, { headers });
      setUsers(res.data.users);
    } catch { toast.error('Errore caricamento utenti.'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const setRoleWithPermissions = (role) => {
    const perms = {
      owner: Object.keys(PERMISSION_LABELS),
      editor: ['tenants', 'billing', 'leads', 'marketing', 'features'],
      viewer: ['tenants', 'leads'],
    };
    setForm(p => ({ ...p, role, permissions: perms[role] || [] }));
  };

  const togglePerm = (perm) => {
    setForm(p => ({
      ...p,
      permissions: p.permissions.includes(perm)
        ? p.permissions.filter(x => x !== perm)
        : [...p.permissions, perm],
    }));
  };

  const create = async () => {
    if (!form.email || !form.name || !form.password) return toast.error('Compila tutti i campi.');
    if (form.password.length < 8) return toast.error('Password min 8 caratteri.');
    setSaving(true);
    try {
      await axios.post(API, form, { headers });
      toast.success(`Utente ${form.email} creato!`);
      setShowCreate(false);
      setForm(EMPTY_FORM);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore.'); }
    finally { setSaving(false); }
  };

  const deleteUser = async (id, email) => {
    if (!window.confirm(`Eliminare l'utente ${email}?`)) return;
    setDeletingId(id);
    try {
      await axios.delete(`${API}/${id}`, { headers });
      toast.success('Utente eliminato.');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore.'); }
    finally { setDeletingId(null); }
  };

  const toggleActive = async (user) => {
    if (user.is_primary) return;
    try {
      await axios.put(`${API}/${user.id}`, { is_active: !user.is_active }, { headers });
      toast.success(user.is_active ? 'Utente disattivato.' : 'Utente riattivato.');
      load();
    } catch { toast.error('Errore.'); }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-6 h-6 border-2 rounded-full animate-spin" style={{ borderColor: 'var(--lux-gold)', borderTopColor: 'transparent' }} />
    </div>
  );

  return (
    <div data-testid="admin-users-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="font-serif text-xl font-light" style={{ color: 'var(--lux-text)' }}>Utenti SuperAdmin</h3>
          <p className="text-xs" style={{ color: 'var(--lux-text-muted)' }}>Gestisci chi ha accesso al pannello di controllo</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="p-2 rounded-sm" style={{ border: '1px solid var(--lux-border-2)', color: 'var(--lux-text-muted)' }}>
            <RefreshCw size={13} />
          </button>
          <button onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium"
            style={{ background: 'var(--lux-gold)', color: '#000' }}>
            <UserPlus size={14} /> Aggiungi Admin
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {users.map((u) => {
          const rc = ROLE_CONFIG[u.role] || ROLE_CONFIG.viewer;
          return (
            <div key={u.id} className="flex items-center gap-4 px-5 py-4 rounded-sm"
              style={{ background: 'var(--lux-surface-1)', border: `1px solid ${u.is_primary ? 'rgba(201,168,76,0.3)' : 'var(--lux-border-1)'}` }}>
              {/* Avatar */}
              <div className="w-9 h-9 rounded-sm flex items-center justify-center shrink-0 font-bold text-sm"
                style={{ background: u.is_primary ? 'rgba(201,168,76,0.15)' : 'var(--lux-surface-3)', color: u.is_primary ? '#c9a84c' : 'var(--lux-text-muted)' }}>
                {u.name?.charAt(0).toUpperCase()}
              </div>

              {/* Info */}
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium" style={{ color: 'var(--lux-text)' }}>{u.name}</p>
                  {u.is_primary && <span className="text-[9px] px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(201,168,76,0.15)', color: '#c9a84c' }}>PRINCIPALE</span>}
                  <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: rc.bg, color: rc.color }}>{rc.label}</span>
                  {!u.is_active && !u.is_primary && <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: 'rgba(100,100,100,0.1)', color: '#666' }}>Disattivato</span>}
                </div>
                <p className="text-xs mt-0.5" style={{ color: 'var(--lux-text-muted)' }}>{u.email}</p>
                {u.permissions && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {u.permissions.map(p => (
                      <span key={p} className="text-[9px] px-1.5 py-0.5 rounded" style={{ background: 'var(--lux-surface-3)', color: 'var(--lux-text-muted-3)' }}>
                        {PERMISSION_LABELS[p] || p}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Last login */}
              {u.last_login && (
                <p className="text-[10px] text-right hidden md:block" style={{ color: 'var(--lux-text-muted-3)' }}>
                  Ultimo: {u.last_login.slice(0, 10)}
                </p>
              )}

              {/* Actions */}
              {!u.is_primary && (
                <div className="flex items-center gap-1.5">
                  <button onClick={() => toggleActive(u)} className="p-1.5 rounded"
                    style={{ background: u.is_active ? 'rgba(5,150,105,0.1)' : 'rgba(100,100,100,0.1)', color: u.is_active ? '#059669' : '#666' }}
                    title={u.is_active ? 'Disattiva' : 'Attiva'}>
                    {u.is_active ? <Check size={13} /> : <X size={13} />}
                  </button>
                  <button onClick={() => deleteUser(u.id, u.email)} disabled={deletingId === u.id} className="p-1.5 rounded"
                    style={{ background: 'rgba(220,38,38,0.1)', color: '#dc2626' }} title="Elimina">
                    <Trash2 size={13} />
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ background: 'rgba(0,0,0,0.75)' }}>
          <div className="w-full max-w-md rounded-sm p-6 space-y-4" style={{ background: 'var(--lux-surface-1)', border: '1px solid var(--lux-border-1)' }}>
            <div className="flex items-center justify-between">
              <h4 className="font-serif text-lg font-light" style={{ color: 'var(--lux-text)' }}>Nuovo Admin</h4>
              <button onClick={() => setShowCreate(false)}><X size={18} style={{ color: 'var(--lux-text-muted)' }} /></button>
            </div>

            {[
              { key: 'name', label: 'Nome completo', type: 'text', placeholder: 'Mario Rossi' },
              { key: 'email', label: 'Email', type: 'email', placeholder: 'mario@example.com' },
              { key: 'password', label: 'Password (min 8 char)', type: 'password', placeholder: '••••••••' },
            ].map(({ key, label, type, placeholder }) => (
              <div key={key}>
                <label className="block text-[10px] uppercase tracking-widest mb-1" style={{ color: 'var(--lux-text-muted)' }}>{label}</label>
                <input type={type} value={form[key]} onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))}
                  placeholder={placeholder} className="w-full px-3 py-2.5 text-sm rounded-sm"
                  style={{ background: 'var(--lux-surface-3)', border: '1px solid var(--lux-border-2)', color: 'var(--lux-text)' }} />
              </div>
            ))}

            <div>
              <label className="block text-[10px] uppercase tracking-widest mb-2" style={{ color: 'var(--lux-text-muted)' }}>Ruolo</label>
              <div className="flex gap-2">
                {['viewer', 'editor', 'owner'].map(r => (
                  <button key={r} onClick={() => setRoleWithPermissions(r)}
                    className="flex-1 py-2 text-xs font-medium rounded-sm"
                    style={{ background: form.role === r ? ROLE_CONFIG[r].bg : 'var(--lux-surface-3)', color: form.role === r ? ROLE_CONFIG[r].color : 'var(--lux-text-muted)', border: `1px solid ${form.role === r ? ROLE_CONFIG[r].color + '40' : 'var(--lux-border-2)'}` }}>
                    {ROLE_CONFIG[r].label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-[10px] uppercase tracking-widest mb-2" style={{ color: 'var(--lux-text-muted)' }}>Permessi</label>
              <div className="grid grid-cols-2 gap-1.5">
                {Object.entries(PERMISSION_LABELS).map(([key, label]) => (
                  <button key={key} onClick={() => togglePerm(key)}
                    className="flex items-center gap-2 px-2.5 py-1.5 rounded text-xs"
                    style={{ background: form.permissions.includes(key) ? 'rgba(201,168,76,0.08)' : 'var(--lux-surface-3)', color: form.permissions.includes(key) ? 'var(--lux-gold)' : 'var(--lux-text-muted)', border: `1px solid ${form.permissions.includes(key) ? 'rgba(201,168,76,0.25)' : 'var(--lux-border-2)'}` }}>
                    {form.permissions.includes(key) ? <Check size={10} /> : <div className="w-2.5 h-2.5 rounded-full" style={{ background: 'var(--lux-border-2)' }} />}
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <button onClick={create} disabled={saving} className="flex-1 py-2.5 text-sm font-semibold"
                style={{ background: 'var(--lux-gold)', color: '#000' }}>
                {saving ? 'Creazione...' : 'Crea Admin'}
              </button>
              <button onClick={() => setShowCreate(false)} className="px-4 py-2.5 text-sm"
                style={{ border: '1px solid var(--lux-border-2)', color: 'var(--lux-text-muted)' }}>Annulla</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
